import React from "react";
import "./crash.scss";
import Context from "../../context";

const rocketImg = "/rocket5.gif";

export default function CrashGame() {
  const { GameState, currentNum, setCurrentTarget } = React.useContext(Context);

  const canvasRef   = React.useRef<HTMLCanvasElement>(null);
  const rocketRef   = React.useRef<HTMLDivElement>(null);
  const multRef     = React.useRef<HTMLSpanElement>(null);
  const flewRef     = React.useRef<HTMLDivElement>(null);
  const containerRef= React.useRef<HTMLDivElement>(null);
  const rafRef      = React.useRef<number>(0);

  // All hot animation state lives in refs — no re-renders during play
  const phaseRef      = React.useRef<string>("BET");
  const multValRef    = React.useRef<number>(1);
  const pointsRef     = React.useRef<{vx:number,vy:number}[]>([]);
  const startTimeRef  = React.useRef<number>(0);
  const crashedRef    = React.useRef<boolean>(false);

  // Camera (virtual → screen mapping)
  const camRef = React.useRef({ xMax: 12, yMax: 3 });

  // Dot scroll offsets (synced to camera pan)
  const dotXOff = React.useRef<number>(0);
  const dotYOff = React.useRef<number>(0);

  // Stars
  const starsRef = React.useRef<{px:number,py:number,r:number,o:number,sp:number}[]>([]);

  // React state only for phase-driven UI elements
  const [gamePhase, setGamePhase] = React.useState<string>("BET");

  /* ── seed stars once ── */
  React.useEffect(() => {
    const arr: {px:number,py:number,r:number,o:number,sp:number}[] = [];
    for (let i = 0; i < 90; i++)
      arr.push({ px: Math.random(), py: Math.random(),
                 r: Math.random()*1.4+0.3, o: Math.random()*0.55+0.1,
                 sp: Math.random()*0.00012+0.00006 });
    starsRef.current = arr;
  }, []);

  /* ── sync multiplier from server ── */
  React.useEffect(() => {
    const v = parseFloat(currentNum) || 1;
    multValRef.current = v;
    if (setCurrentTarget) setCurrentTarget(v);
  }, [currentNum]);

  /* ── phase transitions ── */
  React.useEffect(() => {
    phaseRef.current = GameState;
    setGamePhase(GameState);
    if (GameState === "BET") {
      crashedRef.current  = false;
      pointsRef.current   = [];
      multValRef.current  = 1;
      startTimeRef.current = 0;
      camRef.current = { xMax: 12, yMax: 3 };
      dotXOff.current = 0; dotYOff.current = 0;
      if (rocketRef.current) {
        rocketRef.current.style.opacity = "0";
        rocketRef.current.style.transform = "rotate(-15deg)";
      }
      if (multRef.current)  multRef.current.textContent  = "";
      if (flewRef.current)  flewRef.current.style.display = "none";
    } else if (GameState === "PLAYING") {
      startTimeRef.current = performance.now();
      if (rocketRef.current) rocketRef.current.style.opacity = "1";
      if (flewRef.current)  flewRef.current.style.display = "none";
    } else if (GameState === "GAMEEND") {
      crashedRef.current = true;
      if (rocketRef.current) {
        rocketRef.current.style.opacity = "0";
        rocketRef.current.style.transform = "rotate(35deg) translate(50px,-50px)";
        rocketRef.current.style.transition = "all 0.35s ease";
      }
      if (flewRef.current) flewRef.current.style.display = "block";
    }
  }, [GameState]);

  /* ══════════════════════════════════════════
     Main rAF render loop — runs at 60 fps
  ══════════════════════════════════════════ */
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let prev = performance.now();

    const loop = (now: number) => {
      rafRef.current = requestAnimationFrame(loop);
      const dt  = now - prev; prev = now;
      const ctx = canvas.getContext("2d");
      const box = containerRef.current;
      if (!ctx || !box) return;

      const W = canvas.width  = box.clientWidth  || 400;
      const H = canvas.height = box.clientHeight || 300;
      ctx.clearRect(0, 0, W, H);

      /* ── background ── */
      const bg = ctx.createRadialGradient(W*0.05, H*1.05, 0, W*0.5, H*0.5, W*1.1);
      bg.addColorStop(0,   "#2d0055");
      bg.addColorStop(0.25,"#15002d");
      bg.addColorStop(0.55,"#080018");
      bg.addColorStop(1,   "#010008");
      ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

      /* ── parallax stars ── */
      const ph  = phaseRef.current;
      const mul = multValRef.current;
      const spd = ph === "PLAYING" ? Math.log(Math.max(mul,1))*0.5+0.5 : 0;
      starsRef.current.forEach(s => {
        if (ph === "PLAYING") {
          s.px -= s.sp * dt * spd; s.py += s.sp * dt * spd * 0.4;
          if (s.px < 0) s.px = 1; if (s.py > 1) s.py = 0;
        }
        ctx.beginPath();
        ctx.arc(s.px*W, s.py*H, s.r, 0, Math.PI*2);
        ctx.fillStyle = `rgba(255,255,255,${s.o})`;
        ctx.fill();
      });

      /* ── graph margins ── */
      const PL = W*0.09, PB = H*0.13, PR = W*0.04, PT = H*0.10;
      const GW = W-PL-PR, GH = H-PT-PB;
      const OX = PL, OY = H-PB;

      /* ── axes ── */
      ctx.strokeStyle = "rgba(255,255,255,0.10)";
      ctx.lineWidth = 1; ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(OX, PT); ctx.lineTo(OX, OY); ctx.lineTo(OX+GW, OY);
      ctx.stroke();

      if (ph !== "PLAYING" && ph !== "GAMEEND") {
        /* BET screen dots only */
        ctx.fillStyle = "rgba(100,180,255,0.35)";
        const bDotY = 6, bSpY = H*0.07;
        for (let y = OY; y > PT; y -= bSpY) {
          ctx.beginPath(); ctx.arc(OX-5, y, 2.5, 0, Math.PI*2); ctx.fill();
        }
        ctx.fillStyle = "rgba(255,255,255,0.3)";
        const bDotX = 6, bSpX = W*0.07;
        for (let x = OX; x < OX+GW; x += bSpX) {
          ctx.beginPath(); ctx.arc(x, OY+5, 2.5, 0, Math.PI*2); ctx.fill();
        }
        return;
      }

      /* ── camera smooth tracking ── */
      const cam = camRef.current;
      const elapsed = Math.max((now - startTimeRef.current)/1000, 0.01);
      const txMax = Math.max(elapsed*1.35, 10);
      const tyMax = Math.max(mul*1.45, 3);
      cam.xMax += (txMax - cam.xMax) * 0.04;
      cam.yMax += (tyMax - cam.yMax) * 0.04;

      const toSX = (vx:number) => OX + (vx/cam.xMax)*GW;
      const toSY = (vy:number) => OY - ((vy-1)/Math.max(cam.yMax-1,0.1))*GH;

      /* ── record point ── */
      if (ph === "PLAYING") {
        if (pointsRef.current.length === 0) pointsRef.current.push({ vx: 0, vy: 1 });
        pointsRef.current.push({ vx: elapsed, vy: mul });
        if (pointsRef.current.length > 900) pointsRef.current.shift();
      }
      const pts = pointsRef.current;
      if (pts.length < 2) return;

      /* ── scrolling axis dots synced to camera ── */
      const dotR = Math.max(2, W*0.004);
      const xDotSpacing = Math.max(16, GW * 0.07);
      const yDotSpacing = Math.max(16, GH * 0.09);

      // X dots scroll RIGHT→LEFT as time advances
      const xScrolled = (elapsed / cam.xMax) * GW;
      dotXOff.current = xScrolled % xDotSpacing;

      // Y dots scroll TOP→BOTTOM as multiplier rises
      const yScrolled = ((mul - 1) / Math.max(cam.yMax-1, 0.1)) * GH;
      dotYOff.current = yScrolled % yDotSpacing;

      // Y axis dots: start from top, scroll downward
      ctx.fillStyle = "rgba(100,180,255,0.65)";
      for (let y = PT + dotYOff.current; y < OY; y += yDotSpacing) {
        ctx.beginPath(); ctx.arc(OX-5, y, dotR, 0, Math.PI*2); ctx.fill();
      }
      // X axis dots: start from right, scroll leftward
      ctx.fillStyle = "rgba(255,255,255,0.55)";
      for (let x = OX+GW - dotXOff.current; x > OX; x -= xDotSpacing) {
        ctx.beginPath(); ctx.arc(x, OY+5, dotR, 0, Math.PI*2); ctx.fill();
      }

      /* ── grid labels ── */
      const fs = Math.max(9, W*0.020);
      ctx.font = `${fs}px monospace`;
      const yLabels = [1.5,2,3,5,10,20,50,100,200].filter(v => v >= 1 && v <= cam.yMax*0.92);
      yLabels.forEach(m => {
        const sy = toSY(m);
        // Only show labels that are clearly in the upper visible area
        const fadeThreshold = OY - GH * 0.15;
        if (sy > PT+4 && sy < fadeThreshold) {
          const alpha = Math.min(1, (fadeThreshold - sy) / (GH * 0.1));
          ctx.strokeStyle = `rgba(255,255,255,${0.055 * alpha})`;
          ctx.lineWidth = 1; ctx.setLineDash([5,7]);
          ctx.beginPath(); ctx.moveTo(OX, sy); ctx.lineTo(OX+GW, sy); ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = `rgba(255,255,255,${0.35 * alpha})`;
          ctx.fillText(`${m}x`, OX - ctx.measureText(`${m}x`).width - 6, sy+4);
        }
      });

      /* ── map virtual pts to screen — always pin first point to axis origin ── */
      const sp = pts.map((p, i) => i === 0
        ? { sx: OX, sy: OY }
        : { sx: toSX(p.vx), sy: toSY(p.vy) });
      const last = sp[sp.length-1];
      const crashed = crashedRef.current;
      const lineCol  = crashed ? "#ff1a1a" : "#ff4500";
      const glowCol  = crashed ? "rgba(255,30,0,0.95)" : "rgba(255,110,0,0.95)";

      /* ── fill under curve ── */
      const topY = Math.min(last.sy, sp[0].sy);
      const fillG = ctx.createLinearGradient(0, topY, 0, OY);
      fillG.addColorStop(0,   crashed?"rgba(220,0,0,0.52)":"rgba(210,55,0,0.42)");
      fillG.addColorStop(0.55,crashed?"rgba(130,0,0,0.18)":"rgba(130,35,0,0.14)");
      fillG.addColorStop(1,   "rgba(0,0,0,0)");

      const drawSmooth = (c: CanvasRenderingContext2D, pts: {sx:number,sy:number}[]) => {
        if (pts.length < 2) return;
        c.moveTo(pts[0].sx, pts[0].sy);
        for (let i = 0; i < pts.length - 1; i++) {
          const p0 = pts[Math.max(i-1,0)];
          const p1 = pts[i];
          const p2 = pts[i+1];
          const p3 = pts[Math.min(i+2, pts.length-1)];
          const t = 0.5;
          const cp1x = p1.sx + (p2.sx - p0.sx) * t / 3;
          const cp1y = p1.sy + (p2.sy - p0.sy) * t / 3;
          const cp2x = p2.sx - (p3.sx - p1.sx) * t / 3;
          const cp2y = p2.sy - (p3.sy - p1.sy) * t / 3;
          c.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.sx, p2.sy);
        }
      };

      ctx.beginPath();
      drawSmooth(ctx, sp);
      ctx.lineTo(last.sx, OY);
      ctx.lineTo(sp[0].sx, OY);
      ctx.closePath();
      ctx.fillStyle = fillG; ctx.fill();

      /* ── glow curve (3 passes) ── */
      const passes = [[32,5.5,lineCol],[16,3.5,lineCol],[6,2,crashed?"#ff6666":"#ffbb55"]] as const;
      passes.forEach(([blur,lw,col]) => {
        ctx.save();
        ctx.shadowColor = glowCol; ctx.shadowBlur = blur;
        ctx.lineWidth = lw; ctx.strokeStyle = col;
        ctx.lineJoin = "round"; ctx.lineCap = "round";
        ctx.beginPath();
        drawSmooth(ctx, sp);
        ctx.stroke();
        ctx.restore();
      });

      /* ── tip glow dot ── */
      if (!crashed) {
        const tipG = ctx.createRadialGradient(last.sx,last.sy,0,last.sx,last.sy,14);
        tipG.addColorStop(0,"rgba(255,200,80,0.95)");
        tipG.addColorStop(0.5,"rgba(255,80,0,0.6)");
        tipG.addColorStop(1,"rgba(255,40,0,0)");
        ctx.beginPath(); ctx.arc(last.sx,last.sy,14,0,Math.PI*2);
        ctx.fillStyle=tipG; ctx.fill();
      }

      /* ── rocket ── */
      const rocket = rocketRef.current;
      if (rocket && ph === "PLAYING" && sp.length >= 3) {
        const prev5 = sp[Math.max(0,sp.length-6)];
        const angle = Math.atan2(last.sy-prev5.sy, last.sx-prev5.sx)*180/Math.PI;
        const osc   = Math.sin(now*0.0025)*5;
        const rW    = Math.min(68, W*0.115);
        rocket.style.left      = `${((last.sx-rW*0.5)/W)*100}%`;
        rocket.style.bottom    = `${((H-last.sy-rW*0.5)/H)*100}%`;
        rocket.style.transform = `rotate(${angle+osc}deg)`;
        rocket.style.width     = `${rW}px`;
        rocket.style.height    = `${rW}px`;
        rocket.style.transition= "left 0.06s linear, bottom 0.06s linear, transform 0.09s linear";
      }

      /* ── multiplier text (direct DOM — no re-render) ── */
      if (multRef.current) {
        const disp = Math.max(mul-0.01,1).toFixed(2)+"x";
        if (multRef.current.textContent !== disp) multRef.current.textContent = disp;
      }
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <div className="crash-container" ref={containerRef}>
      <canvas ref={canvasRef} className="crash-canvas" />

      <div ref={rocketRef} className="rocket-wrap">
        <img src={rocketImg} alt="rocket" className="rocket-img" />
      </div>

      <div className={`multiplier-label ${gamePhase === "GAMEEND" ? "crashed" : gamePhase === "PLAYING" ? "flying" : "hidden"}`}>
        <span ref={multRef} className="mult-value" />
        <div ref={flewRef} className="flew-label" style={{display:"none"}}>FLEW AWAY!</div>
      </div>

      {gamePhase === "BET" && (
        <div className="bet-screen">
          <div className="center-logo">
            <img src={rocketImg} alt="aviator" className="logo-rocket" />
            <div className="logo-text">AVIATOR</div>
          </div>
          <div className="loading-container">
            <div className="loading-bar"><div className="loading-fill" /></div>
            <div className="loading-text">PREPARING NEXT ROUND...</div>
          </div>
        </div>
      )}
    </div>
  );
}
